import { useCallback, useState } from 'react'

import AgoraRTC, {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ILocalTrack,
  type NetworkQuality,
} from 'agora-rtc-react'
import { logAgoraEvent, AgoraLogContext } from 'src/lib/datadog/agoraLogger'
import { AgoraCloudProxyMode } from 'src/lib/gql-client'

import { generateChannelName } from '../utils/generateChannelName'

export type NetworkTestState = 'idle' | 'testing' | 'done' | 'error'

export type NetworkTestResult = {
  uplinkQuality: 0 | 1 | 2 | 3 | 4 | 5 | 6
  downlinkQuality: 0 | 1 | 2 | 3 | 4 | 5 | 6
  packetLossRate: number
  currentPacketLossRate: number
  transportDelay: number
  end2EndDelay: number
  receiveBitrate: number
  freezeRate: number
  codecType: string | undefined
}

type Params = {
  roomUid: string
  outboundToken: string
  outboundUid: number
  inboundToken: string
  inboundUid: number
  agoraCloudProxyMode: AgoraCloudProxyMode
}

type Resources = {
  outboundClient: IAgoraRTCClient
  inboundClient: IAgoraRTCClient
  customTrack: ILocalTrack
  audioContext: AudioContext
}

const STATS_COLLECT_MS = 5_000
const USER_PUBLISHED_TIMEOUT_MS = 10_000

const cleanupResources = async ({
  outboundClient,
  inboundClient,
  customTrack,
  audioContext,
}: Resources) => {
  try {
    customTrack.close()
    try {
      await outboundClient.unpublish()
    } catch {
      // ignore
    }
    await outboundClient.leave()
    await inboundClient.leave()
    await audioContext.close()
  } catch {
    // ignore cleanup errors
  }
}

export const useTwoClientNetworkTest = (params: Params) => {
  const [testState, setTestState] = useState<NetworkTestState>('idle')
  const [result, setResult] = useState<NetworkTestResult | null>(null)

  const startTest = useCallback(async () => {
    setTestState('testing')
    setResult(null)

    const appId = import.meta.env.VITE_AGORA_APP_ID as string
    const channelName = generateChannelName(
      params.roomUid,
      'ConnectionTestOutbound',
    )
    const proxyMode =
      params.agoraCloudProxyMode === 'ForceUDP'
        ? 3
        : params.agoraCloudProxyMode === 'ForceTCP'
          ? 5
          : null

    // クライアント生成
    const outboundClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' })
    const inboundClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' })

    // Cloud Proxy 設定
    if (proxyMode !== null) {
      outboundClient.startProxyServer(proxyMode)
      inboundClient.startProxyServer(proxyMode)
    }

    // オシレーター由来のカスタムオーディオトラックを生成（マイク許可不要）
    const audioContext = new AudioContext()
    const oscillator = audioContext.createOscillator()
    const destination = audioContext.createMediaStreamDestination()
    oscillator.connect(destination)
    oscillator.frequency.setValueAtTime(440, audioContext.currentTime)
    oscillator.start()
    const customTrack = AgoraRTC.createCustomAudioTrack({
      mediaStreamTrack: destination.stream.getAudioTracks()[0],
    })

    try {
      await outboundClient.join(
        appId,
        channelName,
        params.outboundToken,
        params.outboundUid,
      )
      await outboundClient.publish([customTrack])

      await inboundClient.join(
        appId,
        channelName,
        params.inboundToken,
        params.inboundUid,
      )

      let timeoutId: ReturnType<typeof setTimeout>
      // inbound が outbound の音声を受信するまで待機
      await new Promise<void>((resolve, reject) => {
        const handler = async (
          user: IAgoraRTCRemoteUser,
          mediaType: 'audio' | 'video',
        ) => {
          if (user.uid === params.outboundUid && mediaType === 'audio') {
            clearTimeout(timeoutId)
            inboundClient.off('user-published', handler)
            await inboundClient.subscribe(user, mediaType)
            resolve()
          }
        }
        timeoutId = setTimeout(() => {
          inboundClient.off('user-published', handler)
          reject(new Error('user-published timeout'))
        }, USER_PUBLISHED_TIMEOUT_MS)
        inboundClient.on('user-published', handler)
      })

      // network-quality イベントを双方から収集
      // let ではなくオブジェクト参照で保持することで TypeScript の制御フロー解析の問題を回避する
      const capturedQuality: {
        outbound: NetworkQuality | null
        inbound: NetworkQuality | null
      } = { outbound: null, inbound: null }

      outboundClient.on('network-quality', stats => {
        capturedQuality.outbound = stats
      })
      inboundClient.on('network-quality', stats => {
        capturedQuality.inbound = stats
      })

      // stats が安定するまで待機
      await new Promise(resolve => setTimeout(resolve, STATS_COLLECT_MS))

      // リモート音声 stats を取得
      const remoteAudioStats = inboundClient.getRemoteAudioStats()
      const audioStats = remoteAudioStats[params.outboundUid]

      const testResult: NetworkTestResult = {
        uplinkQuality: capturedQuality.outbound?.uplinkNetworkQuality ?? 0,
        downlinkQuality: capturedQuality.inbound?.downlinkNetworkQuality ?? 0,
        packetLossRate: audioStats?.packetLossRate ?? 0,
        currentPacketLossRate: audioStats?.currentPacketLossRate ?? 0,
        transportDelay: audioStats?.transportDelay ?? 0,
        end2EndDelay: audioStats?.end2EndDelay ?? 0,
        receiveBitrate: audioStats?.receiveBitrate ?? 0,
        freezeRate: audioStats?.freezeRate ?? 0,
        codecType: audioStats?.codecType,
      }

      setResult(testResult)
      setTestState('done')

      const logContext: AgoraLogContext = {
        roomUid: params.roomUid,
        videoCallUserUid: null,
        scene: 'ConnectionTest',
      }
      const worstQuality = Math.max(
        testResult.uplinkQuality,
        testResult.downlinkQuality,
      )
      const level =
        worstQuality >= 4 || testResult.packetLossRate >= 0.05 ? 'warn' : 'info'
      logAgoraEvent(level, 'network probe test result', {
        ...logContext,
        type: 'network-probe-result',
        uplinkQuality: testResult.uplinkQuality,
        downlinkQuality: testResult.downlinkQuality,
        packetLossRate: testResult.packetLossRate,
        currentPacketLossRate: testResult.currentPacketLossRate,
        transportDelay: testResult.transportDelay,
        end2EndDelay: testResult.end2EndDelay,
        receiveBitrate: testResult.receiveBitrate,
        freezeRate: testResult.freezeRate,
        codecType: testResult.codecType,
      })
    } catch {
      setTestState('error')
    } finally {
      await cleanupResources({
        outboundClient,
        inboundClient,
        customTrack,
        audioContext,
      })
    }
  }, [params])

  return { testState, result, startTest }
}
